import React, { Component, Fragment } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { injectIntl } from "react-intl";
import { IconButton, Tooltip } from "@material-ui/core";
import { Visibility as ViewIcon, Edit as EditIcon } from "@material-ui/icons";
import {
  withModulesManager,
  withHistory,
  formatMessage,
  formatMessageWithValues,
  formatDateFromISO,
  Searcher,
} from "@openimis/fe-core";
import { fetchHealthFacilityEnrollments } from "../actions";
import EnrollmentFilter from "./EnrollmentFilter";
import EnrollmentStatusBadge from "./common/EnrollmentStatusBadge";
import {
  DEFAULT_ENROLLMENT_FILTERS,
  DEFAULT_PAGE_SIZE,
  ROWS_PER_PAGE_OPTIONS,
} from "../constants";
import { filtersToQueryParams, enrollmentRowIdentifier } from "../utils/searcher";
import {
  normalizeFacilityData,
  formatDisplayValue,
} from "../utils/enrollmentData";

class HealthFacilityEnrollmentSearcher extends Component {
  constructor(props) {
    super(props);
    this.rowsPerPageOptions = props.modulesManager.getConf(
      "fe-enrollment",
      "healthFacilityEnrollmentFilter.rowsPerPageOptions",
      ROWS_PER_PAGE_OPTIONS
    );
    this.defaultPageSize = props.modulesManager.getConf(
      "fe-enrollment",
      "healthFacilityEnrollmentFilter.defaultPageSize",
      DEFAULT_PAGE_SIZE
    );
  }

  fetch = (prms) => {
    this.props.fetchHealthFacilityEnrollments(this.props.modulesManager, prms);
  };

  openEdit = (e) => {
    if (this.props.history) {
      this.props.history.push(`/enrollment/healthfacility/${e.uuid}/edit`);
    }
  };

  headers = () => [
    "healthFacilityEnrollment.facilityInfo",
    "healthFacilityEnrollment.type",
    "healthFacilityEnrollment.location",
    "healthFacilityEnrollment.contact",
    "healthFacilityEnrollment.status",
    "healthFacilityEnrollment.enrollmentDate",
    "",
  ];

  sorts = () => [null, null, null, null, ["status", true], ["dateCreated", false], null];

  itemFormatters = () => {
    const { modulesManager, intl, onDoubleClick } = this.props;
    return [
      (e) => {
        const f = normalizeFacilityData(e.facilityData);
        return (
          <>
            <div>{formatDisplayValue(f.name)}</div>
            <small>{formatDisplayValue(f.code)}</small>
          </>
        );
      },
      (e) => formatDisplayValue(normalizeFacilityData(e.facilityData).level),
      (e) => formatDisplayValue(normalizeFacilityData(e.facilityData).locationId),
      (e) => {
        const f = normalizeFacilityData(e.facilityData);
        return formatDisplayValue(f.phone) || formatDisplayValue(f.email);
      },
      (e) => <EnrollmentStatusBadge status={e.status || "DRAFT"} size="small" />,
      (e) => formatDateFromISO(modulesManager, intl, e.dateCreated) || "-",
      (e) => (
        <>
          <Tooltip title={formatMessage(intl, "enrollment", "general.view") || "View"}>
            <IconButton
              size="small"
              onClick={(ev) => {
                ev.stopPropagation();
                onDoubleClick && onDoubleClick(e);
              }}
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {e.status === "DRAFT" && (
            <Tooltip title={formatMessage(intl, "enrollment", "general.edit") || "Edit"}>
              <IconButton
                size="small"
                onClick={(ev) => {
                  ev.stopPropagation();
                  this.openEdit(e);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </>
      ),
    ];
  };

  render() {
    const {
      intl,
      healthFacilityEnrollments,
      healthFacilityEnrollmentsPageInfo,
      fetchingHealthFacilityEnrollments,
      fetchedHealthFacilityEnrollments,
      errorHealthFacilityEnrollments,
      cacheFiltersKey,
      onDoubleClick,
    } = this.props;

    const totalCount =
      healthFacilityEnrollmentsPageInfo?.totalCount ??
      healthFacilityEnrollments?.length ??
      0;

    return (
      <Fragment>
        <Searcher
          module="enrollment"
          cacheFiltersKey={cacheFiltersKey}
          FilterPane={EnrollmentFilter}
          items={healthFacilityEnrollments}
          itemsPageInfo={{ ...healthFacilityEnrollmentsPageInfo, totalCount }}
          fetchingItems={fetchingHealthFacilityEnrollments}
          fetchedItems={fetchedHealthFacilityEnrollments}
          errorItems={errorHealthFacilityEnrollments}
          tableTitle={formatMessageWithValues(
            intl,
            "enrollment",
            "healthFacilityEnrollment.searcher.results",
            { count: totalCount.toLocaleString() }
          )}
          rowsPerPageOptions={this.rowsPerPageOptions}
          defaultPageSize={this.defaultPageSize}
          defaultFilters={DEFAULT_ENROLLMENT_FILTERS}
          defaultOrderBy="-dateCreated"
          fetch={this.fetch}
          rowIdentifier={enrollmentRowIdentifier}
          filtersToQueryParams={filtersToQueryParams}
          headers={this.headers}
          itemFormatters={this.itemFormatters}
          sorts={this.sorts}
          onDoubleClick={(i) => onDoubleClick && onDoubleClick(i)}
        />
      </Fragment>
    );
  }
}

const mapStateToProps = (state) => ({
  healthFacilityEnrollments: state.enrollment?.healthFacilityEnrollments || [],
  healthFacilityEnrollmentsPageInfo:
    state.enrollment?.healthFacilityEnrollmentsPageInfo || {},
  fetchingHealthFacilityEnrollments:
    state.enrollment?.fetchingHealthFacilityEnrollments,
  fetchedHealthFacilityEnrollments:
    state.enrollment?.fetchedHealthFacilityEnrollments,
  errorHealthFacilityEnrollments:
    state.enrollment?.errorHealthFacilityEnrollments,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ fetchHealthFacilityEnrollments }, dispatch);

export default withModulesManager(
  withHistory(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(injectIntl(HealthFacilityEnrollmentSearcher))
  )
);
